import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

// //Actions////
// Student Actions
import * as studentActions from "Store/students/actions";
import {
  setActiveStudent,
  setStudentActionSpinner,
  setStudentsStatistics
} from "Store/students/actions";

import { backgroundTaskStarted } from "Store/backgroundTasks/actions";

import * as globalActions from "Store/global/actions";
import {
  setActionSpinner,
  setActiveCourse,
  setFetchDetailsSpinner,
  setFetchSpinner
} from "Store/global/actions";

import * as studentTypes from "Store/students/constants";

import {
  convertMetaToPagination,
  downloadFile,
  handleFetchCoursesSaga,
  renderApiParameters
} from "Store/global/saga";

// Filter Actions
import * as globalSelectors from "Store/global/selectors";
import { makeSelectActiveCourse, makeSelectCourses } from "Store/global/selectors";
import { makeSelectActiveStudent } from "Store/students/selectors";
import { makeSelectCurrentUser } from "Store/user/selectors";

import {
  addStudentToClassroomApi,
  addStudentToLocationApi,
  assignInstructorToStudentApi,
  createStudentApi,
  createStudentSessionApi,
  deleteStudentApi,
  deleteStudentSessionApi,
  downloadStudentSyllabusApi,
  enrollStudentInCourseApi,
  fetchAllStudentsApi,
  fetchReportPreviewApi,
  fetchStudentApi,
  fetchStudentsApi,
  fetchStudentSessionsApi,
  fetchStudentsStatisticsApi,
  fetchStudentSummaryApi,
  handleSendReportApi,
  handleUpdateStudentReportApi,
  removeStudentFromClassroomApi,
  removeStudentFromLocationApi,
  unAssignInstructorFromStudentApi,
  unEnrollStudentFromCourseApi,
  updateStudentActivationApi,
  updateStudentAddressApi,
  updateStudentApi,
  updateStudentArchivedApi,
  updateStudentBaselineScoresApi,
  updateStudentCourseContextApi,
  updateStudentCourseFlagsApi,
  updateStudentSessionApi,
  updateStudentTargetScoreApi
} from "Store/students/api";
import { generateToastErrors, Toast } from "utils/toast";
import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import Router from "next/router";
import {
  handleFetchStudentCalendarLessonsSaga,
  handleOpenLessonDetailsSaga
} from "Store/students/studentsLessons/saga";
import { unAssignLessonFromStudentApi } from "Store/students/studentsLessons/api";
import {
  deepEqual,
  isNotEmpty,
  removeDuplicateFromArrayObjects,
  rewriteTheSameProperties
} from "utils/objectsHelper";
import { CourseFlags, StudentCourseContext } from "utils/initialObjects";
import moment from "moment";
import { setActiveCourseId } from "utils/ActiveCourseService";
import {
  handleFetchActiveTestScores,
  handleFetchStudentAllTestsSaga
} from "Store/students/studentsTests/saga";
import * as studentTestsActions from "Store/students/studentsTests/actions";
import { deleteStudentTestApi, fetchTestByTestIdApi } from "Store/students/studentsTests/api";
import {
  handleFetchStudentCalendarWorksheetsSaga,
  handleOpenWorksheetDetailsSaga
} from "Store/students/studentsWorksheets/saga";
import { unassignStudentWorksheetsApi } from "Store/students/studentsWorksheets/api";
import { handleFetchClassroomStudents } from "Store/classrooms/actions";
import { makeSelectCurrentInstructor } from "Store/instructors/selectors";

// //Students////

const compareBaselines = (oldBaselines: Baseline[], newBaselines: Baseline[]): boolean => {
  const subjectIds = newBaselines.map((newBaseline) => newBaseline.subject_id);
  const oldBaselinesBySubject = subjectIds.map((id) =>
    oldBaselines.find((oldBaseline) => oldBaseline.subject_id === id)
  );
  return deepEqual(oldBaselinesBySubject, newBaselines);
};

function* handleFetchStudentSaga(action) {
  yield put(studentActions.setStudentSummary(null));
  yield put(setFetchSpinner(true));
  try {
    const activeCourse = yield select(makeSelectActiveCourse);
    if (activeCourse?.course_id === "all_courses") {
      const courses = yield select(makeSelectCourses);
      if (Boolean(courses?.length)) {
        setActiveCourseId(courses[0]?.course_id);
        yield put(setActiveCourse(courses[0]));
      } else {
        yield call(handleFetchCoursesSaga);
      }
    }

    const { data: student, error, status } = yield fetchStudentApi(action.payload);
    if (status === 404) {
      Router.push("/students");
      Toast.error(ERRORS.fetchStudentDetailsNoStudent(action.payload), error);
    }
    yield put(studentActions.setActiveStudent(student));

    yield call(handleFetchStudentSummary, { studentId: action.payload });
    Router.push("/students/[studentId]", `/students/${action.payload}`);
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudent, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleCreateNewStudentSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const currentUser: User = yield select(makeSelectCurrentUser);
    const { payload } = action;
    const { data, error } = yield createStudentApi(payload);

    const keyErrors = !!isNotEmpty(error) && Object.keys(error);

    if (keyErrors.length) {
      keyErrors.map((keyError) => Toast.error(ERRORS.createNewStudent, error[keyError].join(", ")));
      return;
    }

    const activeCourse = yield select(makeSelectActiveCourse);
    if (activeCourse?.course_id !== "all_courses") {
      const { error: enrollmentError } = yield enrollStudentInCourseApi({
        student_id: data.user_id,
        course_id: activeCourse.course_id
      });

      if (enrollmentError) {
        Toast.error(ERRORS.handleManageStudentEnrollmentsSagaAdd, enrollmentError);
        return yield put(setActionSpinner(false));
      }
    }
    if (currentUser.type !== "SUPER_ADMIN" && currentUser.current_location_role === "INSTRUCTOR") {
      const payload = {
        instructors: [currentUser],
        studentId: data.user_id
      };
      yield call(handleAssignInstructorToStudent, payload);
    }

    Toast.success(SUCCESSES.handleAddNewStudent, "");
    if (activeCourse?.course_id === "all_courses") {
      yield call(handleFetchAllStudentsSaga);
    } else {
      yield call(handleFetchStudentsSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.createNewStudent, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

/** ---- REPORTS SECTION ---- */
function* handleUpdateStudentReportSaga(action) {
  const report = action.payload;
  yield put(setActionSpinner(true));
  try {
    const { error } = yield handleUpdateStudentReportApi(report);
    if (error) {
      Toast.error(ERRORS.handleUpdateStudentReportSaga, error);
    } else {
      const activeStudent = yield select(makeSelectActiveStudent);
      delete report.student_id;

      yield put(
        studentActions.setActiveStudent({
          ...activeStudent,
          report
        })
      );
    }
    Toast.success(SUCCESSES.handleUpdateStudentReportSaga);
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentReportSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleSendReportSaga(action) {
  const { payload } = action;
  yield put(setActionSpinner(true));
  try {
    // send report api
    const { error } = yield handleSendReportApi(payload);
    if (error) {
      Toast.error(ERRORS.handleSendReportSaga, error);
    } else {
      Toast.success(SUCCESSES.handleSendReportSaga);
    }
    yield put(studentActions.setReportPreview(null));
  } catch (error) {
    Toast.error(ERRORS.handleSendReportSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchStudentReportPreviewSaga(action) {
  const { payload } = action;
  yield put(globalActions.setFetchSublistSpinner(true));
  try {
    // fetch data
    const { data: report } = yield fetchReportPreviewApi(payload);
    yield put(studentActions.setReportPreview(report));
  } catch (error) {
    Toast.error(ERRORS.fetchReportPreviewSaga, error);
  } finally {
    yield put(globalActions.setFetchSublistSpinner(false));
  }
}

/** ----END OF REPORTS SECTION ---- */

function* handleAddStudentToLocation(action) {
  try {
    const { error } = yield addStudentToLocationApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleAddStudentToLocation, error);
      return false;
    } else {
      return true;
    }
  } catch (error) {
    Toast.error(ERRORS.handleAddStudentToLocation, error);
    return false;
  }
}

// //Student Summary Tab////

function* handleFetchStudentSummary(action) {
  yield put(setActionSpinner(true));
  try {
    const { error, data } = yield fetchStudentSummaryApi(action.studentId);

    if (error) {
      yield put(studentActions.setStudentSummary(null));
      Toast.error(ERRORS.handleFetchStudentSummary, error);
    } else {
      yield put(studentActions.setStudentSummary(data.summary));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentSummary, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

// //Sessions////

function* handleFetchStudentSessions() {
  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    const { error, data } = yield fetchStudentSessionsApi(activeStudent?.id);
    if (error) {
      return Toast.error(ERRORS.handleFetchStudentSessions, error);
    }
    yield put(studentActions.setStudentSessions(data));
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentSessions, error);
  }
}

function* handleUpdateStudentSessionSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield updateStudentSessionApi(action.payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleUpdateStudentSession, error);
    }
    yield call(handleFetchStudentSessions);
    Toast.success(SUCCESSES.handleUpdateStudentSession);

    yield put(globalActions.setFetchSpinner(true));
    yield call(handleFetchStudentSessions);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleUpdateStudentSession, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
    yield put(globalActions.setFetchSpinner(false));
  }
}

function* handleUpdateStudentActivation(action) {
  yield put(setActionSpinner(true));
  try {
    const { payload, source } = action;
    const { error } = yield updateStudentActivationApi(payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateStudentActivationActivationError, error);
    } else {
      Toast.success(SUCCESSES.handleUpdateStudentActivation(payload.activate));
      if (source === "list") {
        yield call(handleFetchStudentsSaga);
      } else {
        yield call(handleFetchStudentSaga, { payload: payload.student_id });
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentActivation, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUnAssigningInstructorFromStudent(action) {
  try {
    const { error } = yield unAssignInstructorFromStudentApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUnAssigningInstructorFromStudent, error);
    }
    yield put(studentActions.removeInstructorFromStudent(action.payload));
  } catch (error) {
    Toast.error(ERRORS.handleUnAssigningInstructorFromStudent, error);
  }
}

// /////////////////////////****Pre-Made Worksheet Sagas***//////////////////////

/* FRESH SAGAS - new, controlled sagas */

function* handleCreateStudentSession(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield createStudentSessionApi(action.payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      Toast.error(ERRORS.handleCreateStudentSession, error);
    } else {
      yield put(studentActions.getStudentSessions());
      Toast.success(SUCCESSES.handleCreateStudentSession);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleCreateStudentSession, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleDeleteMultipleItemsFromStudentSaga(action) {
  yield put(setActionSpinner(true));
  try {
    let response = "";
    for (const [section] of Object.entries(action.payload)) {
      const requestData = { [`student_${section.slice(0, -1)}_ids`]: action.payload[section] };
      switch (section) {
        case "sessions":
          const { error: sessionError } = yield deleteStudentSessionApi(requestData);
          response = sessionError;
          break;
        case "lessons":
          const { data: lessonDdata } = yield unAssignLessonFromStudentApi(requestData);
          response = lessonDdata;
          break;
        case "worksheets":
          const { data: worksheetData } = yield unassignStudentWorksheetsApi(requestData);
          response = worksheetData;
          break;
        case "tests":
          for (const id of requestData.student_test_ids) {
            const { error } = yield call(deleteStudentTestApi, {
              student_test_id: id
            });
            response = error;
          }
          break;
      }
    }

    yield put(studentActions.handleFetchStudentCalendarData(action.studentId));
    if (response?.length > 0) {
      Toast.error(response);
    } else {
      Toast.success(SUCCESSES.handleDeleteMultipleItemsFromStudentSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleDeleteMultipleItemsFromStudentSaga, error.message);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export function* handleFetchStudentCalendarDataSaga() {
  yield put(globalActions.setFetchCalendarSpinner(true));
  try {
    yield call(handleFetchStudentAllTestsSaga);
    yield call(handleFetchStudentSessions);
    yield call(handleFetchStudentCalendarWorksheetsSaga);
    yield call(handleFetchStudentCalendarLessonsSaga);
  } catch (e) {
    Toast.error(e.message);
  } finally {
    yield put(globalActions.setFetchCalendarSpinner(false));
  }
}

function* handleOpenSelectedItemDetailsSaga(action) {
  // @TODO: There should be loader. Need to check at which point it should be placed
  yield put(setFetchDetailsSpinner(true));
  const { type, item } = action.payload;

  try {
    const { id: studentId } = yield select(makeSelectActiveStudent);
    switch (type) {
      case "lessons":
        yield call(handleOpenLessonDetailsSaga, { item });
        break;
      case "worksheets":
        yield call(handleOpenWorksheetDetailsSaga, { item });
        break;
      case "tests":
        // @TODO: it should be refactored when templateTest problems will be available to populate not started section
        const { data: testResponse } = yield fetchTestByTestIdApi(studentId, item.student_test_id);
        yield call(handleFetchActiveTestScores, {
          payload: {
            student_id: studentId,
            student_test_id: item.student_test_id,
            isRealTest: !!item.real_test
          }
        });
        yield put(studentTestsActions.setActiveStudentTest(testResponse));
        break;
      default:
        break;
    }
  } catch (e) {
    Toast.error(ERRORS.handleOpenSelectedItemDetailsSaga, e.message);
  } finally {
    yield put(setFetchDetailsSpinner(false));
  }
}

function* handleUpdateStudentBaselineScoresSaga(action) {
  yield put(setStudentActionSpinner(true));
  try {
    const { error } = yield updateStudentBaselineScoresApi(action);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleUpdateStudentBaselineScoresSaga, error);
    }
    const activeStudent = yield select(makeSelectActiveStudent);

    yield put(studentActions.setActiveStudent({ ...activeStudent, baseline: action.baselines }));
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleUpdateStudentBaselineScoresSaga, error);
  } finally {
    yield put(setStudentActionSpinner(false));
  }
}

export function* handleFetchStudentsSaga() {
  yield put(setFetchSpinner(true));
  yield put(studentActions.setStudents([]));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "students");
    const { data: students, meta, error } = yield fetchStudentsApi(parameters);
    if (error) {
      Toast.error(ERRORS.handleFetchStudents, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "students"));
      yield put(studentActions.setStudents(students));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudents, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

export function* handleFetchAllStudentsSaga() {
  yield put(setFetchSpinner(true));
  yield put(studentActions.setStudents([]));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "students"); //studentsEnrollment?
    const { data: students, meta, error } = yield fetchAllStudentsApi(parameters);
    if (error) {
      Toast.error(ERRORS.handleFetchStudents, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "students"));
      yield put(studentActions.setStudents(students));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudents, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleManageStudentEnrollmentsSaga(action) {
  const currentUser = yield select(makeSelectCurrentUser);
  const currentInstructor = yield select(makeSelectCurrentInstructor);
  const instructor = currentInstructor || currentUser;
  const student = action.payload.student;
  const studentCoursesId = student.instructors.map((instructor) => instructor.course_id);

  const coursesToAssign: string[] = action.payload.add.filter(
    (id) => !studentCoursesId.includes(id)
  );
  if (coursesToAssign.length) {
    for (const courseId of coursesToAssign) {
      yield call(assignInstructorToStudentApi, {
        instructor_id: instructor.id,
        student_id: action.payload.student_id,
        course_id: courseId
      });
    }
  }

  yield put(setActionSpinner(true));
  try {
    let errors = false;
    if (action.payload.add.length > 0) {
      for (let course of action.payload.add) {
        const { error } = yield enrollStudentInCourseApi({
          student_id: action.payload.student_id,
          course_id: course
        });
        if (error) {
          Toast.error(ERRORS.handleManageStudentEnrollmentsSagaAdd, error);
          errors = true;
        }
      }
    }
    if (action.payload.remove.length > 0) {
      for (let course of action.payload.remove) {
        const { error } = yield unEnrollStudentFromCourseApi({
          student_id: action.payload.student_id,
          course_id: course
        });
        if (error) {
          Toast.error(ERRORS.handleManageStudentEnrollmentsSagaRemove, error);
          errors = true;
        }
      }
    }
    if (!errors) {
      Toast.success(SUCCESSES.handleManageStudentEnrollmentsSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleManageStudentEnrollmentsSagaGeneral, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

// THIS FUNCTIONALITY SEEMS TO NOT BE USED
function* handleDeleteStudentSaga(action) {
  try {
    const { id } = action.payload;
    const { error } = yield deleteStudentApi({ student_id: id });
    if (error) {
      return Toast.error(ERRORS.watchForDeleteStudent, error);
    }
    yield call(handleFetchStudentsSaga);
  } catch (error) {
    Toast.error(ERRORS.watchForDeleteStudent, error);
  }
}

function* handleAssignInstructorToStudent(action) {
  try {
    const { instructors, studentId, courseId } = action;
    for (const instructor of instructors) {
      const payload = {
        instructor_id: instructor.id,
        student_id: studentId,
        course_id: courseId
      };
      const { data } = yield call(assignInstructorToStudentApi, payload);
      if (data.status !== "OK") {
        return Toast.error(ERRORS.handleAssignInstructorToStudent, data.message);
      }
    }
    yield call(handleFetchStudentsSaga);
  } catch (error) {
    Toast.error(ERRORS.handleAssignInstructorToStudent, error);
  }
}

export function* handleAssignStudentToClassroomSaga(action) {
  try {
    const { classrooms, strategy, studentIds } = action.payload;
    for (const classroom of classrooms) {
      const payload = {
        class_id: classroom.id,
        strategy,
        student_ids: studentIds
      };
      const { data, error } = yield addStudentToClassroomApi(payload);
      if (error) {
        return Toast.error(ERRORS.handleAddStudentToClass, error);
      }
      if (data?.batch_id) {
        yield put(
          backgroundTaskStarted({
            id: data.batch_id,
            label: `Assigning students${classroom.name ? ` to ${classroom.name}` : ""}`,
            classroomId: classroom.id,
            total: data.total,
            kind: "assign"
          })
        );
      }
    }
  } catch (error) {
    return Toast.error(ERRORS.handleAddStudentToClass, error);
  }
}

export function* handleRemoveStudentFromClassroomSaga(action) {
  try {
    const errors: string[] = [];
    const { classrooms, strategy, studentIds } = action.payload;
    for (const classroom of classrooms) {
      const payload = {
        class_id: classroom.id,
        strategy,
        student_ids: studentIds
      };
      const { data, error } = yield removeStudentFromClassroomApi(payload);

      if (error) {
        errors.push(error);
        continue;
      }
      if (data?.batch_id) {
        yield put(
          backgroundTaskStarted({
            id: data.batch_id,
            label: `Removing students${classroom.name ? ` from ${classroom.name}` : ""}`,
            classroomId: classroom.id,
            total: data.total,
            kind: "remove"
          })
        );
      }
    }
    if (errors.length) {
      return Toast.error(ERRORS.handleRemoveStudentFromClass, errors.join(", "));
    }
  } catch (error) {
    return Toast.error(ERRORS.handleRemoveStudentFromClass, error);
  }
}

function* handleUpdateStudentCourseFlags(newCourseFlags) {
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const activeCourse: Course = yield select(makeSelectActiveCourse);
    const { exclude_from_stats, inactive_student } = student.courses.find(
      (course) => course.course_id === activeCourse.course_id
    );
    const oldCourseFlags = {
      prevent_login: student.prevent_login,
      exclude_from_stats,
      inactive_student
    };
    if (deepEqual(newCourseFlags, oldCourseFlags)) {
      return;
    } else {
      const payload = {
        student_id: student.id,
        inactive_student: false,
        ...newCourseFlags
      };
      const { error } = yield updateStudentCourseFlagsApi(payload);
      if (error) {
        return Toast.error(ERRORS.handleUpdateStudentFlags, error);
      }
    }
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateStudentFlags, error);
  }
}

function* handleArchivedStudentAccount(isArchived) {
  yield put(setActionSpinner(true));
  try {
    const student = yield select(makeSelectActiveStudent);
    const payload = {
      student_id: student.id,
      archived: isArchived
    };
    if (student.archived !== isArchived) {
      const { error } = yield updateStudentArchivedApi(payload);
      if (error) {
        return Toast.error(ERRORS.handleArchiveStudent, error);
      } else {
        Toast.success(SUCCESSES.handleStudentArchived);
      }
    }
  } catch (error) {
    return Toast.error(ERRORS.handleArchiveStudent, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleArchiveStudentsSaga(action: studentTypes.IHandleArchiveStudents) {
  yield put(setActionSpinner(true));
  const { students, shouldArchive, source } = action.payload;
  try {
    let errors = [];
    for (let student of students) {
      if (student.archived !== shouldArchive) {
        const payload = {
          student_id: student.id,
          archived: shouldArchive
        };
        const { error } = yield updateStudentArchivedApi(payload);
        if (error) {
          errors.push(error);
        }
      }
    }
    if (Boolean(errors?.length)) {
      Toast.error(ERRORS.handleArchiveStudentsSaga(shouldArchive));
    } else {
      Toast.success(SUCCESSES.handleArchiveStudentsSaga(shouldArchive));
    }
    if (source === "CLASSROOM") {
      yield put(handleFetchClassroomStudents());
    } else {
      yield call(handleFetchStudentsSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleArchiveStudentsSaga(shouldArchive));
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateStudentCourseContext(courseContext) {
  try {
    const student = yield select(makeSelectActiveStudent);
    const activeCourse: Course = yield select(makeSelectActiveCourse);
    const studentActiveCourse = student.courses.find(
      (course) => course.course_id === activeCourse.course_id
    );
    const oldCourseContext = rewriteTheSameProperties(StudentCourseContext, studentActiveCourse);
    if (deepEqual(oldCourseContext, courseContext)) {
      return;
    }
    const payload = {
      ...courseContext,
      course_end_date: courseContext.course_end_date
        ? moment(courseContext.course_end_date).format("YYYY-MM-DD")
        : "",
      course_start_date: courseContext.course_start_date
        ? moment(courseContext.course_start_date).format("YYYY-MM-DD")
        : "",
      student_id: student.id
    };
    const { error } = yield updateStudentCourseContextApi(payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateCourseContext, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateCourseContext, error);
  }
}

function* handleUpdateStudentTargetScore(target_score: number) {
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const activeCourse: Course = yield select(makeSelectActiveCourse);
    const oldTargetScore = student.courses.find(
      (course) => course.course_id === activeCourse.course_id
    ).target_score;
    if (oldTargetScore === target_score) {
      return;
    }
    const payload = {
      student_id: student.id,
      target_score: Number(target_score)
    };
    const { error } = yield updateStudentTargetScoreApi(payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateStudentTargetScore, error);
    }
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateStudentTargetScore, error);
  }
}

function* handleUpdateStudentReport(action) {
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const payload = {
      ...action,
      student_id: student.id
    };
    const { error } = yield handleUpdateStudentReportApi(payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateStudentReportSaga, error);
    }
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateStudentReportSaga, error);
  }
}

function* handleUpdateStudentAccount(action: studentTypes.IHandleUpdateStudentAccount) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { instructors, classrooms, report, baselines, accountSettings, courseContext } =
      action.payload;

    const student: Student = yield select(makeSelectActiveStudent);
    const activeCourse: Course = yield select(makeSelectActiveCourse);
    const assignedInstructors = student.instructors;
    const assignedClassroom = student.classrooms;
    const courseFlags = rewriteTheSameProperties(CourseFlags, accountSettings);
    const studentCourseContext = rewriteTheSameProperties(StudentCourseContext, courseContext);

    if (assignedInstructors?.length !== instructors?.newInstructors?.length) {
      const newInstructors = removeDuplicateFromArrayObjects(
        instructors.newInstructors,
        assignedInstructors
      );
      const payload = {
        instructors: newInstructors,
        studentId: student.id
      };
      yield call(handleAssignInstructorToStudent, payload);
    }

    const studentInstructorsIds = student.instructors.map((instructor) => instructor.id);
    const instructorsToRemove = instructors.removedInstructors.filter((instructor) =>
      studentInstructorsIds.includes(instructor.id)
    );

    if (instructorsToRemove.length)
      for (const instructor of instructorsToRemove) {
        const payload = {
          instructor_id: instructor.id,
          student_id: student.id
        };
        yield call(handleUnAssigningInstructorFromStudent, { payload });
      }

    if (classrooms?.newClassrooms?.length) {
      const newClassrooms = removeDuplicateFromArrayObjects(
        classrooms.newClassrooms,
        assignedClassroom
      );
      const payload = {
        classrooms: newClassrooms,
        strategy: classrooms.assignStrategy,
        studentIds: [student.id]
      };
      yield call(handleAssignStudentToClassroomSaga, { payload });
    }
    if (classrooms?.removedClassrooms?.length) {
      const payload = {
        classrooms: classrooms.removedClassrooms,
        strategy: classrooms.unassignStrategy,
        studentIds: [student.id]
      };
      yield call(handleRemoveStudentFromClassroomSaga, { payload });
    }
    if (!compareBaselines(student.baseline, baselines)) {
      yield call(handleUpdateStudentBaselineScoresSaga, { baselines, student_id: student.id });
    }
    if (report && report.day.length) {
      yield call(handleUpdateStudentReport, report);
    }
    yield call(handleUpdateStudentCourseFlags, courseFlags);
    yield call(handleArchivedStudentAccount, accountSettings.archived);
    yield call(handleUpdateStudentCourseContext, studentCourseContext);
    yield call(handleUpdateStudentTargetScore, courseContext.target_score);

    const selectedStudentCourse = student.courses.find(
      (course) => course.course_id === activeCourse.course_id
    );

    if (accountSettings.active !== !selectedStudentCourse.active) {
      const payload = {
        activate: false,
        course_id: activeCourse.course_id,
        student_id: student.id
      };
      yield call(handleUpdateStudentActivation, { payload, source: action.source });
    }
    if (action.source === "LIST") {
      const activeCourse = yield select(makeSelectActiveCourse);
      if (activeCourse?.course_id === "all_courses") {
        yield call(handleFetchAllStudentsSaga);
      } else {
        yield call(handleFetchStudentsSaga);
      }
    } else if (action.source === "PROFILE") {
      const { data } = yield fetchStudentApi(student.id);
      yield put(setActiveStudent({ ...data }));
      Toast.success(SUCCESSES.handleUpdateStudentAccount);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentAccount, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateStudent(action: Partial<studentTypes.IHandleUpdateStudent>) {
  yield put(setActionSpinner(true));
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const { error } = yield updateStudentApi({
      ...action.payload,
      student_id: student.id
    });
    if (error) {
      generateToastErrors(error, ERRORS.handleUpdateStudentErrors);
      return false;
    } else {
      yield call(handleFetchStudentSaga, { payload: student.id });
      Toast.success(SUCCESSES.handleUpdateStudent);
      return true;
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudent, error);
    return false;
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateStudentAddress(action: Partial<studentTypes.IHandleUpdateStudentAddress>) {
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const { error } = yield updateStudentAddressApi({
      ...action.payload,
      student_id: student.id
    });
    if (error) {
      Toast.error(ERRORS.handleUpdateStudentAddress, error);
      return false;
    } else {
      Toast.success(SUCCESSES.handleUpdateStudentAddress);
      return true;
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentAddress, error);
    return false;
  }
}

function* handleUpdateStudentProfile(action: studentTypes.IHandleUpdateStudentProfile) {
  yield put(setActionSpinner(true));
  try {
    const student: Student = yield select(makeSelectActiveStudent);
    const { payload, source } = action;
    const { locations, studentProfile, studentAddress, removedLocation } = payload;
    const location_ids = locations.map((location) => location.id);
    // ---STUDENT

    const oldStudent = rewriteTheSameProperties(studentProfile, student);
    if (!deepEqual(studentProfile, oldStudent)) {
      const updateStudentResponse = yield call(handleUpdateStudent, {
        payload: studentProfile
      });

      if (!updateStudentResponse) {
        return;
      }
    }

    // ---ADDRESS
    if (studentAddress && student.addresses) {
      const updateAddressResponse = yield call(handleUpdateStudentAddress, {
        payload: studentAddress
      });
      if (!updateAddressResponse) {
        return;
      }
    }

    // ---ADD LOCATIONS
    const newLocation = location_ids.filter(
      (locationId) => !student.locations.find((location) => location.id === locationId)
    );
    if (!!newLocation.length) {
      const updateLocationsResponse = yield call(handleAddStudentToLocation, {
        payload: {
          location_ids,
          student_id: student.id
        }
      });
      if (!updateLocationsResponse) {
        return;
      } else {
        Toast.success(SUCCESSES.handleAddStudentToLocation);
      }
    }

    if (removedLocation.length) {
      const removedLocationResponse = yield call(handleUnassignStudentFromLocationSaga, {
        locations: removedLocation,
        studentId: student.id
      });
      if (removedLocationResponse) {
        Toast.success(SUCCESSES.handleUnassignLocationFromStudent);
      }
    }
    if (source === "LIST") {
      const activeCourse = yield select(makeSelectActiveCourse);
      if (activeCourse?.course_id === "all_courses") {
        yield call(handleFetchAllStudentsSaga);
      } else {
        yield call(handleFetchStudentsSaga);
      }
    } else if (source === "PROFILE") {
      const { data } = yield fetchStudentApi(student.id);
      yield put(setActiveStudent({ ...data }));
    }
    return Toast.success(SUCCESSES.handleUpdateStudent);
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateStudentAccount, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchStudentsStatisticsSaga() {
  yield put(setFetchDetailsSpinner(true));
  try {
    const { data, error } = yield fetchStudentsStatisticsApi();
    if (error) {
      Toast.error(ERRORS.handleFetchStudentsStatisticsSaga, error);
    } else {
      yield put(setStudentsStatistics(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentsStatisticsSaga, error);
  } finally {
    yield put(setFetchDetailsSpinner(false));
  }
}

function* handleUnassignStudentFromLocationSaga(
  action: Partial<studentTypes.IUnassignStudentFromLocation>
) {
  try {
    const { locations, studentId } = action;
    for (const location of locations) {
      const { error } = yield removeStudentFromLocationApi({
        student_id: studentId,
        location_id: location.id
      });
      if (error) {
        Toast.error(ERRORS.handleRemoveStudentFromLocation, error);
      }
    }
    return true;
  } catch (error) {
    return Toast.error(ERRORS.handleRemoveStudentFromLocation, error);
  }
}

function* handleDownloadStudentSyllabusSaga(
  action: Partial<studentTypes.IHandleDownloadStudentSyllabus>
) {
  try {
    yield put(globalActions.setActionSpinner(true));

    const { body } = action.payload;
    const student: Student = yield select(makeSelectActiveStudent);
    const { data, error } = yield downloadStudentSyllabusApi(student.id, body);
    if (error) {
      yield put(globalActions.setActionSpinner(false));
      return Toast.error(ERRORS.handleRemoveStudentFromLocation, error);
    }
    if (data) {
      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
      yield call(downloadFile, blob, `syllabus.csv`);
    }
    yield put(globalActions.setActionSpinner(false));
    return Toast.success("Export");
  } catch (error) {
    yield put(globalActions.setActionSpinner(false));

    return Toast.error(ERRORS.handleRemoveStudentFromLocation, error);
  }
}

export default function* studentSaga() {
  yield takeLatest(studentTypes.HANDLE_FETCH_STUDENT, handleFetchStudentSaga);
  yield takeLatest(studentTypes.HANDLE_CREATE_NEW_STUDENT, handleCreateNewStudentSaga);

  yield takeLatest(studentTypes.HANDLE_ADD_STUDENT_TO_LOCATION, handleAddStudentToLocation);

  yield takeEvery(studentTypes.HANDLE_CREATE_STUDENT_SESSIONS, handleCreateStudentSession);
  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT_SESSION, handleUpdateStudentSessionSaga);

  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT_ACTIVATION, handleUpdateStudentActivation);

  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT, handleUpdateStudent);

  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT_REPORT, handleUpdateStudentReportSaga);
  yield takeLatest(
    studentTypes.HANDLE_FETCH_STUDENT_REPORT_PREVIEW,
    handleFetchStudentReportPreviewSaga
  );
  yield takeLatest(studentTypes.HANDLE_SEND_STUDENT_REPORT, handleSendReportSaga);

  /* ===================== GENERAL & CALENDAR */
  yield takeLatest(studentTypes.HANDLE_FETCH_STUDENTS, handleFetchStudentsSaga);
  yield takeLatest(studentTypes.HANDLE_FETCH_ALL_STUDENTS, handleFetchAllStudentsSaga);
  yield takeEvery(
    studentTypes.HANDLE_DELETE_MULTIPLE_ITEMS_FROM_STUDENT,
    handleDeleteMultipleItemsFromStudentSaga
  );
  yield takeEvery(
    studentTypes.HANDLE_FETCH_STUDENT_CALENDAR_DATA,
    handleFetchStudentCalendarDataSaga
  );
  yield takeLatest(
    studentTypes.HANDLE_OPEN_SELECTED_ITEM_DETAILS,
    handleOpenSelectedItemDetailsSaga
  );
  yield takeLatest(
    studentTypes.HANDLE_MANAGE_STUDENT_ENROLLMENTS,
    handleManageStudentEnrollmentsSaga
  );

  yield takeLatest(studentTypes.HANDLE_DELETE_STUDENT, handleDeleteStudentSaga);
  yield takeLatest(studentTypes.HANDLE_ARCHIVE_STUDENTS, handleArchiveStudentsSaga);
  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT_PROFILE, handleUpdateStudentProfile);
  yield takeLatest(studentTypes.HANDLE_UPDATE_STUDENT_ACCOUNT, handleUpdateStudentAccount);
  yield takeLatest(
    studentTypes.HANDLE_FETCH_STUDENTS_STATISTICS,
    handleFetchStudentsStatisticsSaga
  );
  yield takeLatest(studentTypes.HANDLE_FETCH_STUDENT_SESSIONS, handleFetchStudentSessions);
  yield takeLatest(
    studentTypes.HANDLE_DOWNLOAD_STUDENT_SYLLABUS,
    handleDownloadStudentSyllabusSaga
  );
}
